"use client";

import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import AnimatedContent from "../AnimatedContent";
import { useTranslation } from "react-i18next";
import SectionContainer from "@/common/SectionContainer";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

interface CaseStat {
  value: string;
  label: string;
  desc: string;
}

interface CaseInfoSection {
  icon: string;
  title: string;
  items: string[];
}

const CaseSection = () => {
  const { t } = useTranslation();
  const stats = t("CASE_SECTION_STATS", { returnObjects: true }) as CaseStat[];
  const infoSections = t("CASE_SECTION_INFO_SECTIONS", {
    returnObjects: true,
  }) as CaseInfoSection[];
  const imageList = t("CASE_SECTION_IMAGE_LIST", {
    returnObjects: true,
  }) as string[];

  return (
    <SectionContainer
      id="case-section"
      className="section-shell bg-white text-gray-900 overflow-hidden"
    >
      <AnimatedContent className="section-inner section-head">
          <h2 className="section-title">{t("CASE_SECTION_TITLE")}</h2>
          <p className="caption text-gray-600 section-lead whitespace-pre-wrap">
            {t("CASE_SECTION_SUB_TITLE")}
          </p>
      </AnimatedContent>

      <AnimatedContent className="relative w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] my-16">
        <Swiper
          modules={[Autoplay, Pagination, Navigation]}
          spaceBetween={20}
          slidesPerView={1}
          breakpoints={{
            768: {
              slidesPerView: 2,
              spaceBetween: 24,
            },
          }}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
          }}
          pagination={{
            clickable: true,
          }}
          navigation
          loop={true}
          className="w-full"
        >
          {imageList.map((image, index) => (
            <SwiperSlide key={index}>
              <div className="relative w-full aspect-16/9">
                <Image
                  src={image}
                  alt={`${t("CASE_SECTION_IMAGE_ALT_PREFIX")} ${index + 1}`}
                  fill
                  quality={85}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className="object-cover"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </AnimatedContent>

      <AnimatedContent className="section-inner flex flex-col">
          <div className="grid grid-cols-1 md:grid-cols-3 text-center border-y border-gray-200 py-16">
            {stats.map((item, i) => (
              <div
                key={item.label}
                className={`flex flex-col items-center ${
                  i < stats.length - 1 ? "md:border-r border-gray-300" : ""
                } px-6 py-6`}
              >
                <h3 className="text-[#207AE8] text-[clamp(2.5rem,5vw,3.5rem)] font-extrabold leading-none">
                  {item.value}
                </h3>
                <p className="text-[clamp(1rem,1.5vw,1.25rem)] font-semibold text-gray-800 mt-2">
                  {item.label}
                </p>
                <p className="text-gray-500 text-[clamp(0.9rem,1.3vw,1rem)] mt-1">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <AnimatedContent className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-10">
            {infoSections.map((section) => (
              <div
                key={section.title}
                className="bg-gray-50 rounded-2xl shadow-sm hover:shadow-md transition-all p-8 md:p-10 flex flex-col md:flex-row md:items-start gap-8"
              >
                <div className="flex flex-col items-center md:w-1/3 lg:w-1/4 gap-4">
                  <div className="relative w-16 h-16 md:w-20 md:h-20">
                    <Image
                      src={section.icon}
                      alt={`${section.title} ${t("CASE_SECTION_ICON_ALT_SUFFIX")}`}
                      fill
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      className="object-contain"
                    />
                  </div>
                  <h4 className="font-semibold text-[clamp(1rem,1.4vw,1.1rem)] text-gray-700 whitespace-nowrap">
                    {section.title}
                  </h4>
                </div>

                <ul className="flex-1 h-full list-disc list-inside text-gray-700 text-[clamp(0.95rem,1.2vw,1.05rem)] space-y-2">
                  {section.items.map((item) => (
                    <li key={item}> {item} </li>
                  ))}
                </ul>
              </div>
            ))}
          </AnimatedContent>
      </AnimatedContent>
    </SectionContainer>
  );
};

export default CaseSection;
